import PropTypes from "prop-types";
import { useState } from "react";

// Custom components
import GrowthBonus from "../GrowthBonus";
import FrequencySelector from "@/PayPlans/components/Commons/FrequencySelector";

// MUI
import { Checkbox, FormControlLabel, Grid, Stack, Typography } from "@mui/material";

const resolveIsEnabledSwitch = ({ innerTiersLength = 0, outerTiersLength = 0 }) => innerTiersLength >= 2 || outerTiersLength >= 2;

const resolveIsDisableFI = ({ model, tabIndex, innerTiersLength = 0, isOnlyFranchiseInOEM }) => {
	const isPreownedTab = model?.tabsProps.tabs[tabIndex] === "Pre-Owned Vehicles";
	const isCombinedFranchises = Boolean(model?.tabsProps?.combineFranchises);
	return innerTiersLength > 1 && (isPreownedTab || isCombinedFranchises || isOnlyFranchiseInOEM);
};

const Content = (props) => {
	const {
		model = null,
		tabIndex = 0,
		sectionId = "",
		isOnlyFranchiseInOEM = false,
		readOnly = false,
		value = null,
		componentErrors = null,
		checkBoxesMapErrors = null,
		isOnlyOneChecked = () => {},
		handleChangeCheckbox = () => {},
		handleUpdateSwitches = () => {},
		handleUpdateTiers = () => {},
		handleValidation = () => {},
		resolveSecondCheck = () => {},
		setCheckBoxesMapErrors = () => {},
	} = props;

	const [disableAddRow, setDisableAddRow] = useState(false);
	const [disableAddColumn, setDisableAddColumn] = useState(false);

	const isCheckBoxesValid = Object.values(checkBoxesMapErrors).some((item) => item);
	const isReadOnly = readOnly || (disableAddRow && disableAddColumn);
	const isSwitchDisabled = isReadOnly || !isCheckBoxesValid;

	const innerTiersLength = value?.tiers?.length;
	const outerTiersLength = value?.tiers?.[0]?.bonuses?.length;

	const isEnableFI = resolveIsDisableFI({ model, tabIndex, innerTiersLength, isOnlyFranchiseInOEM });
	const isFIDisabled = !isEnableFI || (isEnableFI && model?.makes?.[tabIndex]?.tiers?.length === 1);

	const isEnableSwitch = resolveIsEnabledSwitch({
		innerTiersLength,
		outerTiersLength,
	});

	const handleDisableFrequency = (disabledFrequencies, hasSalesPerformance) => {
		setDisableAddRow(disabledFrequencies?.length === 2);
		setDisableAddColumn(!hasSalesPerformance);
	};

	return (
		<Grid container>
			<Grid item xs={12} className={"models-tiered-area"}>
				<Stack direction={"row"} spacing={1.5}>
					<Stack direction={"row"} alignContent={"center"} alignItems={"center"} sx={{ minWidth: "200px" }}>
						<FrequencySelector
							make={value?.make}
							section={"GrowthBonus"}
							sx={{ width: "200px" }}
							disabled={isReadOnly}
							readOnly={isReadOnly}
							onChange={(onChangeValue) => handleUpdateSwitches(sectionId, "frequency", onChangeValue.value, tabIndex)}
							onDisabledFrequency={handleDisableFrequency}
							selected={value?.frequency}
						/>
					</Stack>
					<FormControlLabel
						label={<Typography className="checkbox-text">{"Retail"}</Typography>}
						control={
							<Checkbox
								checked={Boolean(model?.makes?.[tabIndex]?.isRetail)}
								disabled={isSwitchDisabled || !isEnableSwitch || (isOnlyOneChecked() && model?.makes?.[tabIndex]?.isRetail)}
								onChange={() => handleChangeCheckbox(sectionId, "isRetail", tabIndex, model?.makes)}
							/>
						}
					></FormControlLabel>
					<FormControlLabel
						label={
							<Typography className="checkbox-text">{resolveSecondCheck() === "isFleet" ? "Fleet" : "Wholesale"}</Typography>
						}
						control={
							<Checkbox
								checked={Boolean(model?.makes?.[tabIndex]?.[resolveSecondCheck()])}
								disabled={
									(isOnlyOneChecked() && model?.makes?.[tabIndex]?.[resolveSecondCheck()]) ||
									isSwitchDisabled ||
									!isEnableSwitch
								}
								onChange={() => handleChangeCheckbox(sectionId, resolveSecondCheck(), tabIndex, model?.makes)}
							/>
						}
					></FormControlLabel>
					<FormControlLabel
						label={<Typography className="checkbox-text">{"F&I"}</Typography>}
						control={
							<Checkbox
								checked={Boolean(model?.makes?.[tabIndex]?.isFinance)}
								disabled={(isOnlyOneChecked() && model?.makes?.[tabIndex]?.isFinance) || isSwitchDisabled || isFIDisabled}
								onChange={() => handleChangeCheckbox(sectionId, "isFinance", tabIndex, model?.makes)}
							/>
						}
					></FormControlLabel>
				</Stack>
				<GrowthBonus
					{...value}
					options={{
						isRetail: value?.isRetail,
						isFleet: value?.isFleet,
						isFinance: value?.isFinance,
						isWholesale: value?.isWholesale,
					}}
					readOnly={isReadOnly}
					onActionUpdate={(make, tiers) => handleUpdateTiers(sectionId, make, tiers)}
					onActionValidation={(make, isValid) => {
						handleValidation(sectionId, make, isValid);
					}}
					model={model}
					newAndPreOwnedRequired={model?.tabsProps?.newAndPreOwnedRequired}
					disableAddTier={disableAddRow}
					disableAddColumn={disableAddColumn}
					checkBoxesMapErrors={checkBoxesMapErrors}
					setCheckBoxesMapErrors={setCheckBoxesMapErrors}
					componentErrors={componentErrors}
				/>
			</Grid>
		</Grid>
	);
};

Content.propTypes = {
	model: PropTypes.object,
	tabIndex: PropTypes.number,
	sectionId: PropTypes.string,
	isOnlyFranchiseInOEM: PropTypes.bool,
	readOnly: PropTypes.bool,
	value: PropTypes.object,
	componentErrors: PropTypes.object,
	checkBoxesMapErrors: PropTypes.object,
	isOnlyOneChecked: PropTypes.func,
	handleChangeCheckbox: PropTypes.func,
	handleUpdateSwitches: PropTypes.func,
	handleUpdateTiers: PropTypes.func,
	handleValidation: PropTypes.func,
	resolveSecondCheck: PropTypes.func,
	setCheckBoxesMapErrors: PropTypes.func,
};

export default Content;
